// import node module libraries
import React, { useEffect, useState } from "react";
import { Fragment } from "react";
import { Link } from "react-router-dom";

import { useMutation, useQuery } from "@apollo/client/react";

import { Col, Row, Container, Modal, Image, Button } from "react-bootstrap";

// import custom components
import CourseSlider from "components/marketing/pages/courses/CourseSlider";

// import sub components
import HeroHeader from "components/marketing/pages/courses/course-index/HeroHeader";
import ParentGuardianSurvey from "components/dashboard/authentication/surveys/ParentGuardianSurvey";

// import media files
import Logo from "assets/images/brand/logo/logo.svg";
import HealthWorkerSurvey from "components/dashboard/authentication/surveys/HealthWorkerSurvey";
import HealthInstitutionSurvey from "components/dashboard/authentication/surveys/HealthInstitutionSurvey";

import useAuth from "hooks/useAuth";

// GraphQL
import { GET_COURSES } from "helper/graphql/queries";
import { UPDATE_IGNORE_SURVEY } from "helper/graphql/mutations";

const CourseIndex = () => {
  const { isAuthenticated, user } = useAuth();

  const [showSurvey, setShowSurvey] = useState(false);
  const [showSurveyIntro, setShowSurveyIntro] = useState(false);
  const [courses, setCourses] = useState([]);

  const [onIgnoreSurvey] = useMutation(UPDATE_IGNORE_SURVEY);

  console.log("CourseIndex component is rendering");

  // useQuery(GET_COURSES, {
  //   variables: { isActive: true },
  //   onCompleted: (data) => {
  //     console.log('GET_COURSES data:', data);
  //     setCourses(data?.allCourses?.edges?.map((item) => item?.node));
  //   },
  //   onError: (error) => {
  //     console.error('Error fetching courses:', error);
  //   },
  // });

  const { data, loading, error } = useQuery(GET_COURSES, {
    variables: { isActive: true },
  });

  useEffect(() => {
    if (data?.allCourses?.edges) {
      setCourses(data.allCourses.edges.map(item => item.node));
    }
  }, [data]);

  const filterCoursesByTag = (tag) => {
    return courses?.filter(course => course?.tags?.includes(tag));
  };

  const handleClose = () => {
    setShowSurvey(false);
  };

  const handleIntroClose = () => {
    setShowSurveyIntro(false);
    onIgnoreSurvey({
      variables: { guid: user?.guid, isFirstTimeLogin: false }
    });
  };

  useEffect(() => {
    setShowSurveyIntro(isAuthenticated && user?.isFirstTimeLogin);
  }, [user, isAuthenticated]);

  // Course sections configuration with brand colors
  const courseSections = [
    {
      title: "Intensive Bootcamps",
      description: "Comprehensive training programs designed for rapid skill development",
      tag: "Bootcamp",
      icon: "fe-zap",
      gradient: "linear-gradient(135deg, #F68521 0%, #ff9d4d 100%)"
    },
    {
      title: "Master Classes",
      description: "Advanced courses led by industry experts and thought leaders",
      tag: "Master Class",
      icon: "fe-award",
      gradient: "linear-gradient(135deg, #1d9eb0 0%, #3fc1d3 100%)"
    },
    {
      title: "For Parents & Caregivers",
      description: "Essential knowledge for supporting children with rheumatic conditions",
      tag: "For Parents",
      icon: "fe-heart",
      gradient: "linear-gradient(135deg, #70a553 0%, #8bc86f 100%)"
    },
    {
      title: "For Healthcare Workers",
      description: "Professional development for medical practitioners and care providers",
      tag: "For Healthcare Workers",
      icon: "fe-activity",
      gradient: "linear-gradient(135deg, #1d9eb0 0%, #70a553 100%)"
    }
  ];

  return (
    <Fragment>
      <HeroHeader isAuth={isAuthenticated} />

      {/* Mission Statement Section */}
      {/* <section className="mission-section py-5 bg-light">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8} className="text-center">
              <div className="mission-content">
                <div className="mission-icon mb-4">
                  <i className="fe fe-target display-4 text-primary"></i>
                </div>
                <h2 className="mission-title mb-4">Our Mission</h2>
                <p className="mission-text lead">
                  Hope Arthritis Foundation is dedicated to promoting holistic care for 
                  children and young people with inflammatory rheumatic diseases through 
                  world-class education and expert-led training programs.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section> */}

      {/* Course Sections */}
      {courseSections.map((section, index) => {
        const sectionCourses = filterCoursesByTag(section.tag);
        
        // Only render if there are courses
        if (!sectionCourses || sectionCourses.length === 0) return null;

        return (
          <section 
            key={section.tag} 
            className={`course-section ${index % 2 === 0 ? 'bg-white' : 'bg-light'}`}
          >
            <Container>
              <Row className="mb-5">
                <Col lg={8}>
                  <div className="section-header">
                    <div className="section-icon mb-3" style={{ background: section.gradient }}>
                      <i className={`fe ${section.icon}`}></i>
                    </div>
                    <h2 className="section-title mb-3">{section.title}</h2>
                    <p className="section-description">{section.description}</p>
                  </div>
                </Col>
                <Col lg={4} className="d-flex align-items-end justify-content-lg-end">
                  <Link 
                    to="/academy/courses/course-filter-page/" 
                    className="btn btn-outline-primary btn-view-all"
                  >
                    View All <i className="fe fe-arrow-right ms-2"></i>
                  </Link>
                </Col>
              </Row>
              <Row>
                <Col>
                  <CourseSlider courses={sectionCourses} />
                </Col>
              </Row>
            </Container>
          </section>
        );
      })}

      {/* Call to Action Section */}
      <section className="cta-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <div className="cta-content text-center">
                <h2 className="cta-title mb-4">Ready to Start Your Learning Journey?</h2>
                <p className="cta-description mb-5">
                  Join hundreds of healthcare professionals and caregivers advancing 
                  their knowledge in pediatric rheumatology.
                </p>
                <div className="cta-actions">
                  {!isAuthenticated ? (
                    <Link to="/authentication/sign-in/" className="btn btn-cta-primary btn-lg me-3">
                      Get Started Free
                    </Link>
                  ) : (
                    <Link to="/courses" className="btn btn-cta-primary btn-lg me-3">
                      Browse All Courses
                    </Link>
                  )}
                  <Link to="https://hopearthritisfoundation.com" className="btn btn-cta-secondary btn-lg" target="_blank">
                    Learn More About Us
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Survey Modals */}
      <Modal
        show={showSurveyIntro}
        onHide={handleIntroClose}
        backdrop="static"
        keyboard={false}
        centered
        className="survey-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <div className="w-100">
            <Link to="/" className="d-inline-block mb-3">
              <Image src={Logo} height={40} alt="Hope Arthritis Academy" />
            </Link>
            <h3 className="modal-title-custom mb-0">Welcome Survey</h3>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          <div className="survey-intro-content">
            <p className="mb-3">
              <strong>Dear Participant,</strong>
            </p>
            <p>
              This survey has been developed by Hope Arthritis Foundation to help achieve 
              its mission of promoting holistic care to children and young people with 
              inflammatory rheumatic diseases.
            </p>
            <p>
              We would be very grateful if you would complete this short questionnaire. 
              Your responses will help us understand the various needs in your setting 
              and country. There are no right or wrong answers, and all information will 
              be treated as confidential.
            </p>
            <div className="consent-box p-3 bg-light rounded">
              <p className="mb-0">
                <strong>Consent:</strong> Information obtained may be used to draft policy 
                papers and research articles. Do you consent to have the information you 
                share used for this purpose?
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="outline-secondary"
            onClick={handleIntroClose}
            className="btn-modal-secondary"
          >
            Decline
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowSurveyIntro(false);
              setShowSurvey(true);
            }}
            className="btn-modal-primary"
          >
            Accept & Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSurvey}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        size="xl"
        centered
        className="survey-modal-main"
      >
        <Modal.Body className="p-0">
          {user?.isPatient && (
            <ParentGuardianSurvey name="Parent/Guardian Survey" onComplete={handleClose} />
          )}
          {user?.isHealthcareWorker && (
            <HealthWorkerSurvey name="Health Worker Survey" onComplete={handleClose} />
          )}
          {user?.isOrgAdmin && (
            <HealthInstitutionSurvey name="Health Institution Survey" onComplete={handleClose} />
          )}
        </Modal.Body>
      </Modal>

      <style jsx>{`
        /* Mission Section */
        .mission-section {
          padding: 5rem 0;
        }

        .mission-content {
          padding: 2rem;
        }

        .mission-icon i {
          opacity: 0.1;
        }

        .mission-icon .text-primary {
          color: #F68521 !important;
        }

        .mission-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1e293b;
          letter-spacing: -0.02em;
        }

        .mission-text {
          font-size: 1.125rem;
          line-height: 1.8;
          color: #64748b;
        }

        /* Course Sections */
        .course-section {
          padding: 5rem 0;
        }

        .section-header {
          animation: fadeInUp 0.6s ease-out;
        }

        .section-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          border-radius: 16px;
          color: white;
          font-size: 24px;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .section-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          letter-spacing: -0.02em;
        }

        .section-description {
          font-size: 1.0625rem;
          color: #64748b;
          line-height: 1.7;
          max-width: 600px;
        }

        .btn-view-all {
          border-color: #F68521;
          color: #F68521;
          border-radius: 10px;
          padding: 0.625rem 1.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-view-all:hover {
          background: #F68521;
          border-color: #F68521;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.25);
        }

        /* CTA Section */
        .cta-section {
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 50%, #70a553 100%);
          padding: 6rem 0;
          position: relative;
          overflow: hidden;
        }

        .cta-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="2" fill="white" opacity="0.1"/></svg>');
          background-size: 50px 50px;
        }

        .cta-content {
          position: relative;
          z-index: 1;
        }

        .cta-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: white;
          letter-spacing: -0.02em;
        }

        .cta-description {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.95);
          line-height: 1.7;
        }

        .cta-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-cta-primary {
          background: white;
          color: #F68521;
          border: none;
          border-radius: 12px;
          padding: 0.875rem 2rem;
          font-weight: 700;
          transition: all 0.3s ease;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .btn-cta-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
          color: #F68521;
        }

        .btn-cta-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
          border-radius: 12px;
          padding: 0.875rem 2rem;
          font-weight: 700;
          transition: all 0.3s ease;
        }

        .btn-cta-secondary:hover {
          background: white;
          color: #F68521;
          transform: translateY(-3px);
        }

        /* Modal Styles */
        .survey-modal .modal-content {
          border-radius: 16px;
          border: none;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .modal-title-custom {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .survey-intro-content p {
          color: #64748b;
          line-height: 1.7;
        }

        .consent-box {
          border-left: 4px solid #F68521;
        }

        .btn-modal-primary {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          border: none;
          border-radius: 10px;
          padding: 0.625rem 1.5rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-modal-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.35);
        }

        .btn-modal-secondary {
          border-radius: 10px;
          padding: 0.625rem 1.5rem;
          font-weight: 600;
        }

        .survey-modal-main .modal-content {
          border-radius: 20px;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive */
        @media (max-width: 991px) {
          .mission-section {
            padding: 3rem 0;
          }

          .course-section {
            padding: 3rem 0;
          }

          .cta-section {
            padding: 4rem 0;
          }

          .mission-title,
          .cta-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.75rem;
          }

          .cta-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .btn-cta-primary,
          .btn-cta-secondary {
            width: 100%;
          }

          .btn-view-all {
            margin-top: 1rem;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default CourseIndex;